import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LangContext";
import PageMeta from "@/components/PageMeta";
import { toast } from "sonner";
import { ArrowRight, Loader2, Lock } from "lucide-react";

export default function AdminLoginPage() {
  const { user, login } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [email, setEmail] = useState("admin@royalhouse.de");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user && user.role === "admin") navigate("/admin", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success(t("admin.login.welcome"));
      navigate("/admin", { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : t("admin.login.failed");
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center pt-32 pb-16 px-6" data-testid="admin-login-page">
      <PageMeta pageKey="admin" />
      <div className="w-full max-w-md">
        <div className="border border-white/10 p-8 md:p-12 bg-[#0A2244]/40 backdrop-blur-sm">
          <div className="w-12 h-12 border border-white/20 flex items-center justify-center mb-8">
            <Lock size={18} strokeWidth={1.5} />
          </div>
          <p className="text-xs tracking-[0.3em] uppercase text-white/50 mb-3">{t("admin.login.eyebrow")}</p>
          <h1 className="font-display text-3xl md:text-4xl font-light uppercase tracking-tight leading-tight mb-10">
            {t("admin.login.title")}
          </h1>

          <form onSubmit={submit} className="space-y-7" data-testid="admin-login-form">
            <label className="block">
              <span className="text-[11px] tracking-[0.25em] uppercase text-white/50">{t("admin.login.email")}</span>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="admin-email"
                className="mt-2 w-full bg-transparent border-0 border-b border-white/20 focus:border-white text-white py-3 outline-none"
              />
            </label>
            <label className="block">
              <span className="text-[11px] tracking-[0.25em] uppercase text-white/50">{t("admin.login.password")}</span>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="admin-password"
                className="mt-2 w-full bg-transparent border-0 border-b border-white/20 focus:border-white text-white py-3 outline-none"
              />
            </label>

            <button
              type="submit"
              disabled={loading}
              data-testid="admin-login-submit"
              className="w-full rh-btn-primary inline-flex items-center justify-center gap-3 bg-white text-[#051630] font-bold tracking-[0.18em] uppercase text-xs px-6 py-4 border border-white disabled:opacity-50"
            >
              {loading ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  <span>{t("admin.login.submitting")}</span>
                </>
              ) : (
                <>
                  <span>{t("admin.login.submit")}</span>
                  <ArrowRight size={16} />
                </>
              )}
            </button>
          </form>
        </div>
        <p className="text-center text-xs text-white/40 mt-6 tracking-wider">
          {t("common.protected_area")}
        </p>
      </div>
    </div>
  );
}
